'use client';

import { createContext, useContext, type ReactNode } from 'react';

import type { LinkClickEventData } from '@/hooks/use-analytics';

/**
 * A link-location value doubles as the analytics source and the navigation
 * classifier input (see `useIframeNavigation`). Fine-grained values like
 * `'flight-card'` / `'vacation-package-card'` carry analytics detail (the
 * Widget Conversion Funnel filters on them);
 * navigation treats everything non-`'text'` as carousel-like.
 */
export type LinkLocation = Exclude<LinkClickEventData['source'], 'text'>;

const LinkLocationContext = createContext<LinkLocation | null>(null);

interface LinkLocationProviderProps {
  location: LinkLocation;
  children: ReactNode;
}

export function LinkLocationProvider({ location, children }: LinkLocationProviderProps) {
  return <LinkLocationContext.Provider value={location}>{children}</LinkLocationContext.Provider>;
}

export function useLinkLocation(): LinkLocation | null {
  return useContext(LinkLocationContext);
}

const LinkToolNameContext = createContext<string | undefined>(undefined);

/** The tool whose result renders these links, for their click analytics. */
export const LinkToolNameProvider = LinkToolNameContext.Provider;

/** Undefined for a link outside any tool result, such as assistant prose. */
export function useLinkToolName(): string | undefined {
  return useContext(LinkToolNameContext);
}
